<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiChip, UiButton } from "@shopexperts/shared";
import type { AdminPayoutRequest } from "@shopexperts/legacy-api";
import { formatCurrency } from "@/v2Projects/utils/display";
import { payoutStatusChip, formatPayoutRequestDate } from "@/components/expert/wallet/payoutRequestDisplay";
import { expertProfileRoute } from "@/app/components/expert-manager/wallet/transactionParty";
import { buildAllocationSourceRows } from "@/app/components/expert-manager/wallet/useAdminWalletTransaction";
import AdminTransactionAllocations from "@/app/components/expert-manager/wallet/AdminTransactionAllocations.vue";

const props = defineProps<{
  request: AdminPayoutRequest;
  showOrganization?: boolean;
  isActing?: boolean;
}>();

const emit = defineEmits<{
  (e: "approve", request: AdminPayoutRequest): void;
  (e: "reject", request: AdminPayoutRequest): void;
}>();

const chip = computed(() => payoutStatusChip[props.request.status]);

const expertRoute = computed(() => {
  const expertId = props.request.organization?.expert_id;
  return typeof expertId === "number" ? expertProfileRoute(expertId) : null;
});

const metaLine = computed(() => {
  const parts: string[] = [];
  if (props.request.requested_by) {
    parts.push(`Requested by ${props.request.requested_by.name} (${props.request.requested_by.email})`);
  }
  parts.push(formatPayoutRequestDate(props.request.created_at));
  return parts.filter(Boolean).join(" · ");
});

const decisionLine = computed(() => {
  if (!props.request.decided_at) return null;
  const parts: string[] = [];
  if (props.request.decided_by) {
    parts.push(`Decided by ${props.request.decided_by.name}`);
  }
  parts.push(formatPayoutRequestDate(props.request.decided_at));
  return parts.join(" · ");
});

const transactionRoute = computed(() =>
  props.request.wallet_transaction_id
    ? { name: "admin-wallet-transactions", query: { search: String(props.request.wallet_transaction_id) } }
    : null,
);

const allocationRows = computed(() =>
  buildAllocationSourceRows(props.request.available_allocations ?? [], (cents) =>
    formatCurrency(cents, props.request.currency),
  ),
);

const unallocatedLabel = computed(() => {
  const cents = props.request.unallocated_available_cents;
  return typeof cents === "number" && cents > 0 ? formatCurrency(cents, props.request.currency) : null;
});

const hasAllocationDetails = computed(
  () => allocationRows.value.length > 0 || unallocatedLabel.value !== null,
);
</script>

<template>
  <li class="flex flex-col gap-2 rounded-xl border border-se-gray-200 bg-se-white p-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-3">
        <UiText
          as="span"
          variant="h5"
          :text="formatCurrency(request.amount_cents, request.currency)"
          class="font-semibold text-se-dark"
        />
        <UiChip
          :text="chip.label"
          variant="plain"
          size="xs"
          rounded="full"
          :bg-color-class="chip.bgClass"
          :text-color-class="chip.textClass"
        />
      </div>
      <div v-if="request.status === 'pending'" class="flex items-center gap-2">
        <UiButton
          variant="outline"
          label="Reject"
          :disabled="isActing"
          @click="emit('reject', request)"
        />
        <UiButton
          variant="primary"
          label="Approve"
          :disabled="isActing"
          @click="emit('approve', request)"
        />
      </div>
    </div>

    <p class="flex flex-wrap items-center gap-1 text-xs text-se-gray-500">
      <template v-if="showOrganization && request.organization">
        <RouterLink
          v-if="expertRoute"
          :to="expertRoute"
          class="text-xs text-se-gray-500 underline hover:text-se-dark"
        >
          {{ request.organization.name }}
        </RouterLink>
        <span v-else>{{ request.organization.name }}</span>
        <span>·</span>
      </template>
      <span>{{ metaLine }}</span>
    </p>
    <p v-if="decisionLine" class="flex flex-wrap items-center gap-1 text-xs text-se-gray-500">
      <span>{{ decisionLine }}</span>
      <template v-if="transactionRoute">
        <span>·</span>
        <RouterLink :to="transactionRoute" class="text-xs text-se-gray-500 underline hover:text-se-dark">
          Transaction #{{ request.wallet_transaction_id }}
        </RouterLink>
      </template>
    </p>
    <UiText
      v-if="request.notes"
      as="p"
      variant="override"
      :text="`Expert note: ${request.notes}`"
      class="text-sm text-se-gray-600"
    />
    <UiText
      v-if="request.public_notes"
      as="p"
      variant="override"
      :text="`Note to expert: ${request.public_notes}`"
      class="text-sm text-se-gray-600"
    />
    <UiText
      v-if="request.internal_notes"
      as="p"
      variant="override"
      :text="`Internal note: ${request.internal_notes}`"
      class="text-sm text-se-gray-500"
    />

    <AdminTransactionAllocations
      v-if="hasAllocationDetails"
      :rows="allocationRows"
      :unallocated-label="unallocatedLabel"
      heading-tooltip="The released holds and credits whose available funds covered this payout, matched oldest first."
      subject="payout"
      flush
      class="mt-1"
    />
  </li>
</template>
